import { useEffect, useRef } from 'react'
import { usePlayer } from '../../context/PlayerContext'
import { getReleaseNeighbors } from '../../api/releases'

// How close to the end of the queue we allow before topping up.
const REFILL_THRESHOLD = 2
// How many similar releases to append per top-up.
const RELEASES_PER_REFILL = 6
// How many neighbors to pull from the API per request.
const NEIGHBOR_FETCH_LIMIT = 40

/**
 * Headless controller that powers "radio" mode. When the player has an active
 * radio seed and the queue is about to run dry, it pulls the seed's
 * audio-similar neighbors and appends a few of them — producing an endless,
 * taste-based station. When the seed's neighbors are exhausted it re-seeds from
 * the currently playing release so the station keeps drifting through the map.
 */
export function RadioController() {
  const { radioSeedId, queue, currentIndex, addToQueue, isInQueue } = usePlayer()

  const fetchingRef = useRef(false)
  const seedRef = useRef<number | null>(null)
  const appendedRef = useRef<Set<number>>(new Set())

  // Reset accumulated state whenever a fresh radio session starts.
  useEffect(() => {
    if (radioSeedId == null) {
      seedRef.current = null
      appendedRef.current = new Set()
      return
    }
    seedRef.current = radioSeedId
    appendedRef.current = new Set([radioSeedId])
  }, [radioSeedId])

  useEffect(() => {
    if (radioSeedId == null) return
    const remaining = queue.length - 1 - currentIndex
    if (remaining > REFILL_THRESHOLD) return
    if (fetchingRef.current) return

    fetchingRef.current = true
    ;(async () => {
      try {
        let seed = seedRef.current ?? radioSeedId
        let added = 0

        // Try the current seed; if it yields nothing new, drift the seed to the
        // track that's playing now and try once more.
        for (let attempt = 0; attempt < 2 && added === 0; attempt++) {
          const { releases } = await getReleaseNeighbors(seed, NEIGHBOR_FETCH_LIMIT, false)
          for (const rel of releases) {
            if (added >= RELEASES_PER_REFILL) break
            if (appendedRef.current.has(rel.id) || isInQueue(rel.id)) {
              appendedRef.current.add(rel.id)
              continue
            }
            if (addToQueue(rel, 0)) {
              appendedRef.current.add(rel.id)
              added += 1
            }
          }
          if (added === 0) {
            const current = queue[currentIndex]
            if (current && current.releaseId !== seed) {
              seed = current.releaseId
              seedRef.current = seed
            } else {
              break
            }
          }
        }
      } catch {
        // Network/seed error — radio simply stops topping up; existing queue plays out.
      } finally {
        fetchingRef.current = false
      }
    })()
  }, [radioSeedId, queue, currentIndex, addToQueue, isInQueue])

  return null
}
